import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { allCategories } from '@/utils/article';
import { hotKeywords } from '@/mocks/categories';

const NAV = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category/scandal', hasMenu: true },
  { label: '搜索', to: '/search' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to.split('/').slice(0, 2).join('/'));

  return (
    <header className="sticky top-0 z-50">
      <div className="w-full bg-primary-950 text-primary-100 overflow-hidden">
        <div className="w-full px-4 md:px-8 h-9 flex items-center gap-3">
          <span className="flex items-center gap-1 text-secondary-400 text-xs font-label font-semibold whitespace-nowrap">
            <i className="ri-fire-fill text-sm" /> 今日热榜
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex w-max anim-marquee">
              {[0, 1].map((dup) => (
                <div key={dup} className="flex items-center">
                  {hotKeywords.map((word) => (
                    <Link
                      key={`${dup}-${word}`}
                      to={`/search?q=${encodeURIComponent(word)}`}
                      className="px-3 text-xs text-primary-100/80 hover:text-secondary-400 transition-colors whitespace-nowrap"
                    >
                      {word}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <nav className="w-full bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
              <i className="ri-fire-line" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg text-foreground-950">黑料网曝大瓜</span>
              <span className="font-label text-[10px] tracking-[0.2em] text-primary-600 mt-0.5">GOSSIP · 娱乐爆料</span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <li
                key={item.label}
                className="relative"
                onMouseEnter={() => item.hasMenu && setMenuOpen(true)}
                onMouseLeave={() => item.hasMenu && setMenuOpen(false)}
              >
                {item.hasMenu ? (
                  <button
                    type="button"
                    onClick={() => navigate('/category/scandal')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 cursor-pointer ${
                      isActive('/category') ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                    }`}
                  >
                    {item.label}
                    <i className="ri-arrow-down-s-line text-base" />
                  </button>
                ) : (
                  <Link
                    to={item.to}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors block ${
                      isActive(item.to) ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}

                {item.hasMenu && menuOpen && (
                  <div className="absolute left-0 top-full pt-2 w-64">
                    <div className="bg-background-50 border border-background-200 rounded-lg p-2 anim-fade-up">
                      {allCategories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/category/${c.slug}`}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors"
                        >
                          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                            <i className={`${c.icon} text-base`} />
                          </span>
                          <span className="flex flex-col">
                            <span className="text-sm font-medium text-foreground-900">{c.name}</span>
                            <span className="text-xs text-foreground-500 line-clamp-1">{c.subs.slice(0, 2).join(' · ')}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center bg-background-100 border border-background-200 rounded-md pl-3 pr-1 h-10 w-56 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-400 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜明星 / 大瓜"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 px-2"
              />
              <button type="submit" className="h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 anim-fade-up">
            <form onSubmit={submitSearch} className="flex items-center bg-background-100 border border-background-200 rounded-md pl-3 pr-1 h-11 mb-3">
              <i className="ri-search-line text-foreground-400 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜明星 / 大瓜"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 px-2"
              />
              <button type="submit" className="h-9 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap">
                搜索
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-md bg-background-100 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {allCategories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-700"
                >
                  <i className={`${c.icon} text-primary-600`} /> {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}